import { Component } from 'react'
import Qs from 'qs'
import { sum, values, flatten } from 'lodash'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { captureException } from 'utils/SentryHelpers'

import * as DriftAppMetricsActions from 'app/modules/driftAppMetrics/actions'
import * as OrgSelectors from 'app/modules/org/selectors'
import * as GatesSelectors from 'app/modules/gates/selectors'
import * as PlaybookSelectors from 'app/modules/playbook/selectors'
import * as PlaybookActions from 'app/modules/playbook/actions'
import { playbookHomeLoaded } from 'app/modules/driftAppMetrics/helper'
import { getHasMarketplaceModalCutover, getPlaybooksTableCutover } from 'app/modules/access/api'
import CategoryHeader from './Header'
import PlaybooksTable from './PlaybooksTableContainer'
import { PlaybooksTabs, PlaybookTabStatusOptions, tableStates } from './PlaybooksTableContainer/constants'
import { fetchCategoryCount } from './api/playbooks'
import { Tabs, Button, ToolTip } from '@driftt/tide-core-legacy'
import ACCESS from 'utils/Access'
import IconSVG from 'svg-inline-react'
import websiteCtaSrc from '!svg-inline!images/svg/website-cta.svg'
import { APP_URL, CONVERSATION_API } from 'constants/ResourcesConstants'

import SubNav from 'components/shared/Subnav'
import SUB_NAV_NODES from './navigation'

import './style.styl'

const WELCOME_MSG_OPT_IN_ID = 'welcome-msg-opt-in'
const OPT_IN_URL = `${CONVERSATION_API}/playbooks/welcome-msg-opt-in`
const OPT_IN_CLICK_CONFIG = {
  clickId: WELCOME_MSG_OPT_IN_ID,
  url: OPT_IN_URL,
  successMessage: 'Successfully converted your welcome message to playbooks! Refresh to see them',
  errorMessage: 'Could not convert your welcome message to playbooks. Please contact support for help',
}

const getBackfillDate = ({
  hasProPlan,
  hasStarterPlan,
  hasCompanyPlan,
}) => {
  let date = '7th'
  if (hasCompanyPlan) {
    date = '28th'
  } else if (hasProPlan) {
    date = '21st'
  } else if (hasStarterPlan) {
    date = '14th'
  }
  return `Tuesday morning, August ${date}`
}

let foreverDismissOnboarding = false
try {
  if (window.localStorage) {
    foreverDismissOnboarding = window.localStorage.getItem('dismiss-playbook-list-education')
  }
} catch(e) {}

const ConsolidatedWelcomeMsgOptIn = ({
  optInPending,
  actions: { asyncButtonPress },
  hasProPlan,
  hasStarterPlan,
  hasCompanyPlan,
}) => (
  <div className="welcome-msg-opt-in--container">
    <div className="welcome-msg-opt-in--info-group">
      <IconSVG src={websiteCtaSrc} className="welcome-msg-opt-in--icon"/>
      <p>Want to manage your Welcome Messages in Playbooks? Us too. So we’re moving them there on {getBackfillDate({ hasProPlan, hasStarterPlan, hasCompanyPlan })}. Want it early? Get early access…</p>
    </div>
    <div className="welcome-msg-opt-in--button-group">
      <Button type="tertiary" className="opt-in-button" target="_blank" rel="noopener noreferrer" href="https://help.drift.com/article/welcome-message-playbook/">Learn More</Button>
      {ACCESS.isAdmin() && <Button loading={optInPending} className="opt-in-button" disabled={optInPending} type="primary" onClick={asyncButtonPress.bind(null, OPT_IN_CLICK_CONFIG)}>Get it now</Button>}
      {!ACCESS.isAdmin() &&
        <ToolTip component="Ask your Drift admin to opt in" flow="up">
          <Button type="primary" disabled={true} className="opt-in-button">Get it now</Button>
        </ToolTip>
      }
    </div>
  </div>
)

const ConnectedWelcomeMsgOptIn = connect(
  state => ({
    optInPending: PlaybookSelectors.asyncButtonPending(state, `${WELCOME_MSG_OPT_IN_ID}`),
    orgId: OrgSelectors.orgId(state),
    hasProPlan: GatesSelectors.hasProPlan(state),
    hasStarterPlan: GatesSelectors.hasStarterPlan(state),
    hasCompanyPlan: GatesSelectors.hasCompanyPlan(state),
    hasStandardTrial: GatesSelectors.hasStandardTrial(state),
    isOnFreePlan: GatesSelectors.isOnFreePlan(state),
  }),
  dispatch => ({
    actions: bindActionCreators({
      asyncButtonPress: PlaybookActions.asyncButtonPress,
    }, dispatch)
  })
)(ConsolidatedWelcomeMsgOptIn)

const getValidQueryCategory = categoryFromUrlQuery => {
  const validTabs = Object.keys(PlaybookTabStatusOptions)
  const validCategories = flatten(
    validTabs.map(tab => PlaybookTabStatusOptions[tab].map(status => `${tab}_${status.value}`))
  )
  if (validCategories.includes(categoryFromUrlQuery)) {
    return categoryFromUrlQuery
  } else {
    return tableStates.CHAT_ACTIVE
  }
}

const getMarketplaceCutover = async (orgId) => {
  const { HAS_MARKETPLACE_MODAL } = await getHasMarketplaceModalCutover(orgId)
  return HAS_MARKETPLACE_MODAL
}

const getTabData = (isAdmin, hasMarketplaceModal) => {
  return isAdmin ?
  [
    {
      title: PlaybooksTabs[0].label,
      component: () => <PlaybooksTable activeTab={PlaybooksTabs[0].value} activeTabLabel={PlaybooksTabs[0].label} hasMarketplaceModal={hasMarketplaceModal} />,
    },
    {
      title: PlaybooksTabs[1].label,
      component: () => <PlaybooksTable activeTab={PlaybooksTabs[1].value} activeTabLabel={PlaybooksTabs[1].label} hasMarketplaceModal={hasMarketplaceModal} />,
    },
    {
      title: PlaybooksTabs[3].label,
      component: () => <PlaybooksTable activeTab={PlaybooksTabs[3].value} activeTabLabel={PlaybooksTabs[3].label} hasMarketplaceModal={hasMarketplaceModal} />,
    }
  ] :
  [
    {
      title: PlaybooksTabs[2].label,
      component: () => <PlaybooksTable activeTab={PlaybooksTabs[2].value} activeTabLabel={PlaybooksTabs[2].label} hasMarketplaceModal={hasMarketplaceModal} />,
    },
  ]
}

class PlaybookListWrapper extends Component {
  constructor(props) {
    super(props)

    const category = Qs.parse(window.location.search, { ignoreQueryPrefix: true }).category
    const [tab, status] = getValidQueryCategory(category).split('_')
    const hasMarketplaceModal = getMarketplaceCutover(props.orgId)

    this.state = {
      initialCountFetchDone: false,
      hasNoPlaybooks: false,
      showPlaybookCreator: false,
      category,
      tabIndex: 0,
      tabData: getTabData(ACCESS.hasScopes('org_admin'), hasMarketplaceModal),
      hasMarketplaceModal: hasMarketplaceModal,
      isMarketplaceOpen: false
    }
  }

  componentDidMount() {
    this.trackPbHomeVisit()
    this.checkPlaybooksTableCutover()

    const { category, tabData } = this.state
    const tabIndex = category ? tabData.findIndex(tabItem => tabItem.title.toUpperCase() === category.toUpperCase()) : 0
    this.setState({ tabIndex: tabIndex })
  }

  componentWillMount() {
    this.checkHasPlaybooks()

    const { location = {}, router = null } = this.props
    const query = location.query || {}

    const forceRouterReplace = ACCESS.isAdmin() && !!router

    // We default to chat anyway, but make the route reflect that.
    if (!query.category && forceRouterReplace) {
      router.replace({
        pathname: '/playbooks',
        search: '?category=chat'
      })
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.location.search !== prevProps.location.search) {
      const category = Qs.parse(window.location.search, { ignoreQueryPrefix: true }).category
      const { tabData } = this.state
      this.setState({
        tabIndex: category ? tabData.findIndex(tabItem => tabItem.title.toUpperCase() === category.toUpperCase()) : 0
      })
    }
  }

  checkPlaybooksTableCutover = async () => {
    try {
      const { PLAYBOOKS_TABLE_IN_PEW = false } = await getPlaybooksTableCutover()

      if (PLAYBOOKS_TABLE_IN_PEW) {
        window.location.replace(`${APP_URL}/playbooks/list`)
      }
    } catch(err) {
      console.warn(err)
    }
  }

  trackPbHomeVisit = () => {
    let plan = 'FREE'
    if (this.props.hasCompanyPlan) {
      plan = 'ENTERPRISE'
    } else if (this.props.hasProPlan) {
      plan = 'PRO'
    } else if (this.props.hasStarterPlan) {
      plan = 'STANDARD'
    } else if (this.props.hasStandardTrial) {
      plan = 'STANDARD_TRIAL'
    }

    this.props.actions.sendDriftAppMetric(playbookHomeLoaded(plan))
  }

  handleNonAdminOnNewPlaybookClick = () => {
    window.location.replace('/marketplace/playbooks/OUTBOUND_SEQUENCE', '_self')
  }

  checkHasPlaybooks = async () => {
    try {
      const data = await fetchCategoryCount(!ACCESS.isAdmin() && !ACCESS.hasAnyScope('SALES_SEQUENCES'))
      const hasNoPlaybooks = sum(values(data)) === 0
      if (hasNoPlaybooks) {
        // TODO: change this to react router once we move this into marketplace web
        this.setState({
          hasNoPlaybooks: true,
          isMarketplaceOpen: true
        })
      }
      this.setState({
        initialCountFetchDone: true,
      })
    } catch(err) {
      captureException(err)
      console.warn(err)
    }
  }

  onActivateTab = (index) => {
    this.setState({
      tabIndex: index
    }, this.updateCurrentRoute(this.state.tabData[index].title.toLowerCase()))
  }

  updateCurrentRoute = category => () => {
    this.props.router.replace({
      pathname: 'playbooks',
      query: { category }
    })
  }

  render() {
    const {
      initialCountFetchDone,
      tabIndex,
      tabData,
      isMarketplaceOpen
    } = this.state

    const {
      hasConsolidatedWelcomeMsg,
      location
    } = this.props

    if (!initialCountFetchDone) {
      return null
    }
    const showSubnav = ACCESS.isAdmin()
    const showSequences = ACCESS.isChatUser()

    function getHeaderTitle() {
      if (!location) {
        return 'Playbooks'
      }

      if (showSequences) {
        return 'Sequences'
      }

      const splitSearchLocation = location.search.split('=')
      const getSearchType = splitSearchLocation.length > 1 && !!splitSearchLocation[1]
        ? splitSearchLocation[1]
        : 'Chat'

      return `${getSearchType.charAt(0).toUpperCase()}${getSearchType.slice(1)} playbooks`
    }

    return (
      <div className="playbooks-list-wrapper">
        {showSubnav && (
          <SubNav
            title="Playbooks"
            open={true}
            nodes={SUB_NAV_NODES()}
          />
        )}

        <div className="playbooks-list hide-tabs">
          <CategoryHeader heading={getHeaderTitle()} handleNonAdminOnClick={this.handleNonAdminOnNewPlaybookClick} hasMarketplaceModal={this.state.hasMarketplaceModal} isMarketplaceOpen={isMarketplaceOpen} />
          {!hasConsolidatedWelcomeMsg && <ConnectedWelcomeMsgOptIn />}
          <Tabs data={tabData} activeTab={tabIndex} controlled={true} onActivateTab={this.onActivateTab} />
        </div>
      </div>
    )
  }
}

const mapStateToProps = state => ({
  hasProPlan: GatesSelectors.hasProPlan(state),
  hasStarterPlan: GatesSelectors.hasStarterPlan(state),
  hasCompanyPlan: GatesSelectors.hasCompanyPlan(state),
  hasStandardTrial: GatesSelectors.hasStandardTrial(state),
  isOnFreePlan: GatesSelectors.isOnFreePlan(state),
  hasConsolidatedWelcomeMsg: GatesSelectors.makeHasGate('has_consolidated_welcome_msg')(state),
  orgId: OrgSelectors.orgId(state),
})

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({
    sendDriftAppMetric: DriftAppMetricsActions.sendDriftAppMetric
  }, dispatch),
})

export default connect(mapStateToProps, mapDispatchToProps)(PlaybookListWrapper)



// WEBPACK FOOTER //
// ./src/scripts/components/PlaybookList/index.js